"use client";

import type { ReactNode } from "react";
import type { EntityStoryStatus } from "@/actions/story";
import { StoryAvatarRing } from "@/features/stories/components/story-avatar-ring/story-avatar-ring";
import { useStoryViewer } from "@/features/stories/components/story-viewer/story-viewer-provider";
import type { StoryAuthorType } from "@/features/stories/lib/types";

type EntityStoryRingProps = {
  authorType: StoryAuthorType;
  /** userId for user stories; String(entityId) otherwise. */
  authorId: string;
  name: string;
  avatarUrl?: string | null;
  href?: string | null;
  status: EntityStoryStatus | null;
  /** Outer diameter in px; forwarded to StoryAvatarRing. */
  size?: number;
  /** Forwarded to StoryAvatarRing; person heroes pass "squircle". */
  shape?: "circle" | "squircle";
  children: ReactNode;
};

/**
 * Story ring for entity/profile hero surfaces. Wraps the existing avatar/logo
 * and, when the viewer has active stories to watch, renders the tappable
 * gradient/gray ring that opens the single-author viewer. When there are no
 * active stories (or no ring to show) it renders the avatar with no ring and
 * no tap handler. Status is resolved server-side (getStoryRingStatus) and
 * passed in as a prop.
 */
export function EntityStoryRing({
  authorType,
  authorId,
  name,
  avatarUrl,
  href,
  status,
  size,
  shape,
  children,
}: EntityStoryRingProps) {
  const { openViewer } = useStoryViewer();

  if (!status?.hasActive) {
    return (
      <StoryAvatarRing state="none" size={size} shape={shape}>
        {children}
      </StoryAvatarRing>
    );
  }

  return (
    <StoryAvatarRing
      state={status.hasUnseen ? "unseen" : "seen"}
      size={size}
      shape={shape}
      label={`View ${name}'s story`}
      onPress={() =>
        openViewer({
          authors: [
            {
              type: authorType,
              id: authorId,
              name,
              avatarUrl: avatarUrl ?? null,
              href: href ?? null,
            },
          ],
          startAuthorIndex: 0,
        })
      }
    >
      {children}
    </StoryAvatarRing>
  );
}
